import {
  AfterViewInit,
  Component,
  ElementRef,
  HostListener,
  NgZone,
  OnDestroy,
  ViewChild,
} from '@angular/core';
import * as THREE from 'three';
import { RoomEnvironment } from 'three/examples/jsm/environments/RoomEnvironment.js';
import { SVGLoader } from 'three/examples/jsm/loaders/SVGLoader.js';

@Component({
  selector: 'app-interactive-logo',
  templateUrl: './interactive-logo.html',
  styleUrl: './interactive-logo.scss',
})
export class InteractiveLogo implements AfterViewInit, OnDestroy {
  @ViewChild('container', { static: true })
  private containerRef!: ElementRef<HTMLDivElement>;

  @ViewChild('fallback', { static: true })
  private fallbackRef!: ElementRef<HTMLImageElement>;

  private scene?: THREE.Scene;
  private camera?: THREE.PerspectiveCamera;
  private renderer?: THREE.WebGLRenderer;
  private environmentTexture?: THREE.Texture;
  private interactionGroup?: THREE.Group;
  private contentGroup?: THREE.Group;

  private resizeObserver?: ResizeObserver;
  private intersectionObserver?: IntersectionObserver;
  private animationFrame: number | null = null;

  private isVisible = true;
  private isDestroyed = false;

  private targetRotationX = 0;
  private targetRotationY = 0;
  private currentRotationX = 0;
  private currentRotationY = 0;

  private readonly reducedMotion = window.matchMedia(
    '(prefers-reduced-motion: reduce)',
  ).matches;

  constructor(private readonly ngZone: NgZone) {}

  ngAfterViewInit(): void {
    this.ngZone.runOutsideAngular(() => this.initializeScene());
  }

  @HostListener('window:pointermove', ['$event'])
  onPointerMove(event: PointerEvent): void {
    if (this.reducedMotion || !this.interactionGroup) {
      return;
    }

    const section = this.containerRef.nativeElement.closest('.start-page');

    if (!(section instanceof HTMLElement)) {
      return;
    }

    const rect = section.getBoundingClientRect();

    if (
      event.clientY < rect.top ||
      event.clientY > rect.bottom ||
      event.clientX < rect.left ||
      event.clientX > rect.right
    ) {
      this.resetRotation();
      return;
    }

    const pointerX = ((event.clientX - rect.left) / rect.width - 0.5) * 2;
    const pointerY = ((event.clientY - rect.top) / rect.height - 0.5) * 2;

    this.targetRotationY = THREE.MathUtils.clamp(pointerX, -1, 1) * 0.32;
    this.targetRotationX = THREE.MathUtils.clamp(pointerY, -1, 1) * 0.21;

    this.startRenderLoop();
  }

  @HostListener('window:blur')
  onWindowBlur(): void {
    this.resetRotation();
  }

  @HostListener('document:mouseleave')
  onDocumentLeave(): void {
    this.resetRotation();
  }

  private resetRotation(): void {
    this.targetRotationX = 0;
    this.targetRotationY = 0;
    this.startRenderLoop();
  }

  ngOnDestroy(): void {
    this.isDestroyed = true;

    if (this.animationFrame !== null) {
      cancelAnimationFrame(this.animationFrame);
    }

    this.resizeObserver?.disconnect();
    this.intersectionObserver?.disconnect();

    const materials = new Set<THREE.Material>();

    this.contentGroup?.traverse((object) => {
      if (
        !(object instanceof THREE.Mesh) &&
        !(object instanceof THREE.LineSegments)
      ) {
        return;
      }

      object.geometry.dispose();

      const meshMaterials = Array.isArray(object.material)
        ? object.material
        : [object.material];

      meshMaterials.forEach((material) => materials.add(material));
    });

    materials.forEach((material) => material.dispose());
    this.environmentTexture?.dispose();
    this.renderer?.dispose();
    this.renderer?.domElement.remove();
  }

  private initializeScene(): void {
    const container = this.containerRef.nativeElement;
    const width = Math.max(container.clientWidth, 1);
    const height = Math.max(container.clientHeight, 1);

    this.scene = new THREE.Scene();
    this.camera = new THREE.PerspectiveCamera(32, width / height, 0.1, 10_000);

    try {
      this.renderer = new THREE.WebGLRenderer({
        alpha: true,
        antialias: true,
        powerPreference: 'high-performance',
      });
    } catch {
      return;
    }

    this.renderer.setSize(width, height, false);
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 1.75));
    this.renderer.outputColorSpace = THREE.SRGBColorSpace;
    this.renderer.toneMapping = THREE.NeutralToneMapping;
    this.renderer.toneMappingExposure = 0.86;

    const environmentGenerator = new THREE.PMREMGenerator(this.renderer);
    this.environmentTexture = environmentGenerator.fromScene(
      new RoomEnvironment(),
      0.04,
    ).texture;
    this.scene.environment = this.environmentTexture;
    environmentGenerator.dispose();

    Object.assign(this.renderer.domElement.style, {
      position: 'absolute',
      inset: '0',
      width: '100%',
      height: '100%',
      display: 'block',
      pointerEvents: 'none',
    });

    container.appendChild(this.renderer.domElement);

    this.createLights();

    this.interactionGroup = new THREE.Group();
    this.contentGroup = new THREE.Group();
    this.interactionGroup.add(this.contentGroup);
    this.scene.add(this.interactionGroup);

    this.observeResize();
    this.observeVisibility();
    void this.loadLogo();
  }

  private async loadLogo(): Promise<void> {
    if (!this.camera || !this.renderer || !this.contentGroup) {
      return;
    }

    try {
      const svg = await new SVGLoader().loadAsync('/main_logo.svg');

      if (this.isDestroyed) {
        return;
      }

      const frontMaterial = new THREE.MeshPhysicalMaterial({
        color: 0xf2f1ee,
        roughness: 0.42,
        metalness: 0.04,
        clearcoat: 0.24,
        clearcoatRoughness: 0.3,
        specularIntensity: 0.7,
        specularColor: 0xffffff,
        envMapIntensity: 0.48,
        side: THREE.DoubleSide,
      });

      const sideMaterial = new THREE.MeshPhysicalMaterial({
        color: 0x7f898e,
        roughness: 0.46,
        metalness: 0.08,
        clearcoat: 0.12,
        clearcoatRoughness: 0.4,
        specularIntensity: 0.55,
        envMapIntensity: 0.35,
        side: THREE.DoubleSide,
      });

      for (const path of svg.paths) {
        const shapes = SVGLoader.createShapes(path);

        for (const shape of shapes) {
          const geometry = new THREE.ExtrudeGeometry(shape, {
            depth: 36,
            bevelEnabled: true,
            bevelThickness: 1.8,
            bevelSize: 1.25,
            bevelOffset: 0,
            bevelSegments: 2,
            curveSegments: 14,
            steps: 1,
          });

          geometry.scale(1, -1, 1);
          geometry.computeVertexNormals();

          this.contentGroup.add(
            new THREE.Mesh(geometry, [frontMaterial, sideMaterial]),
          );
        }
      }

      this.centerLogo();
      this.positionCamera();
      this.render();

      this.fallbackRef.nativeElement.classList.add(
        'interactive-logo__fallback--hidden',
      );
    } catch (error) {
      console.error('Не удалось создать 3D-логотип:', error);
    }
  }

  private centerLogo(): void {
    if (!this.contentGroup) {
      return;
    }

    const center = new THREE.Box3()
      .setFromObject(this.contentGroup)
      .getCenter(new THREE.Vector3());

    this.contentGroup.position.set(-center.x, -center.y, -center.z);
  }

  private positionCamera(): void {
    if (!this.camera || !this.contentGroup) {
      return;
    }

    const container = this.containerRef.nativeElement;
    const size = new THREE.Box3()
      .setFromObject(this.contentGroup)
      .getSize(new THREE.Vector3());

    if (size.x === 0 || size.y === 0) {
      return;
    }

    const aspect =
      Math.max(container.clientWidth, 1) / Math.max(container.clientHeight, 1);
    const verticalFov = THREE.MathUtils.degToRad(this.camera.fov);
    const horizontalFov =
      2 * Math.atan(Math.tan(verticalFov / 2) * aspect);
    const distanceForHeight = size.y / (2 * Math.tan(verticalFov / 2));
    const distanceForWidth = size.x / (2 * Math.tan(horizontalFov / 2));
    const distance = Math.max(distanceForWidth, distanceForHeight) * 1.08;

    this.camera.position.set(0, 0, distance);
    this.camera.lookAt(0, 0, 0);
  }

  private createLights(): void {
    if (!this.scene) {
      return;
    }

    const hemisphereLight = new THREE.HemisphereLight(
      0xffffff,
      0x536b78,
      0.72,
    );

    /*
     * Основной студийный свет создаёт яркое отражение на лицевой плоскости.
     */
    const keyLight = new THREE.DirectionalLight(0xffffff, 2.4);
    keyLight.position.set(-260, 330, 620);

    /*
     * Холодный боковой источник проявляет торцы при наклоне.
     */
    const sideLight = new THREE.DirectionalLight(0xb9d7e5, 1.15);
    sideLight.position.set(520, -80, 320);

    /*
     * Контровой свет даёт узкую светлую линию на дальней фаске.
     */
    const rimLight = new THREE.DirectionalLight(0xffffff, 1.35);
    rimLight.position.set(-250, 280, -600);

    this.scene.add(hemisphereLight, keyLight, sideLight, rimLight);
  }

  private startRenderLoop(): void {
    if (
      this.animationFrame !== null ||
      !this.isVisible ||
      this.isDestroyed ||
      !this.interactionGroup
    ) {
      return;
    }

    const animate = (): void => {
      if (this.isDestroyed || !this.isVisible || !this.interactionGroup) {
        this.animationFrame = null;
        return;
      }

      const easing = 0.075;
      this.currentRotationX +=
        (this.targetRotationX - this.currentRotationX) * easing;
      this.currentRotationY +=
        (this.targetRotationY - this.currentRotationY) * easing;

      this.interactionGroup.rotation.set(
        this.currentRotationX,
        this.currentRotationY,
        0,
      );
      this.render();

      const distance =
        Math.abs(this.targetRotationX - this.currentRotationX) +
        Math.abs(this.targetRotationY - this.currentRotationY);

      if (distance > 0.0001) {
        this.animationFrame = requestAnimationFrame(animate);
        return;
      }

      this.currentRotationX = this.targetRotationX;
      this.currentRotationY = this.targetRotationY;
      this.interactionGroup.rotation.set(
        this.currentRotationX,
        this.currentRotationY,
        0,
      );
      this.render();
      this.animationFrame = null;
    };

    this.animationFrame = requestAnimationFrame(animate);
  }

  private render(): void {
    if (this.renderer && this.scene && this.camera) {
      this.renderer.render(this.scene, this.camera);
    }
  }

  private observeResize(): void {
    const container = this.containerRef.nativeElement;

    this.resizeObserver = new ResizeObserver(() => {
      if (!this.renderer || !this.camera) {
        return;
      }

      const width = Math.max(container.clientWidth, 1);
      const height = Math.max(container.clientHeight, 1);

      this.camera.aspect = width / height;
      this.camera.updateProjectionMatrix();
      this.renderer.setSize(width, height, false);
      this.positionCamera();
      this.render();
    });

    this.resizeObserver.observe(container);
  }

  private observeVisibility(): void {
    this.intersectionObserver = new IntersectionObserver(
      ([entry]) => {
        this.isVisible = entry.isIntersecting;

        if (!this.isVisible && this.animationFrame !== null) {
          cancelAnimationFrame(this.animationFrame);
          this.animationFrame = null;
          return;
        }

        if (this.isVisible) {
          this.render();
          this.startRenderLoop();
        }
      },
      { threshold: 0.01 },
    );

    this.intersectionObserver.observe(this.containerRef.nativeElement);
  }
}
